import { createSelector } from "@reduxjs/toolkit";
import type { IRootState } from "../app/store";
import { RoomSideBarTabs, ROOM_SIDEBAR_TABS_BY_MODULE, ROOM_SIDEBAR_DEFAULT_TAB_BY_MODULE } from "@multiconsult-group/multiplacer-core";
import {
  selectAllRoomSideBarTabs,
  selectRoomSideBarTabs,
  selectActiveSpaceHasNoWarnings,
} from "../DesignTab/designSelector";
import { selectHttpSingleRoomFull } from "../Room/httpRoomSelectors";
import {
  isValidationPanelTab,
  groupValidationPanelTabs,
  validationTabForColor,
} from "./validationPanelTabs";
import { ROOM_SIDEBAR_TAB_CONFIG } from "./roomSidebarTabConfig";
import { FaClipboardCheck } from "react-icons/fa";
import { InvalidIcon } from "../PingToPyCeiling";

export const selectActiveValidationColor = (state: IRootState) => state.design.activeRoomColoringId;
export const selectEffectiveRoomTab = createSelector(
  [(state: IRootState) => state.design.selectedRoomTab, selectActiveValidationColor,
    (state: IRootState) => state.design.activeModule],
  (tab, color, module) => {
    const effective = isValidationPanelTab(tab) ? (validationTabForColor(color) ?? tab) : tab;
    if (!effective || !module || !ROOM_SIDEBAR_TABS_BY_MODULE[module].includes(effective)) {
      return module ? ROOM_SIDEBAR_DEFAULT_TAB_BY_MODULE[module] : undefined;
    }
    return effective;
  }
);
export const selectCeilingCommentCount = createSelector(
  [selectHttpSingleRoomFull],
  (room) => room?.ceilingComments?.length ?? 0
);
export const selectVisibleRoomTabs = createSelector(
  [
    selectAllRoomSideBarTabs,
    selectRoomSideBarTabs,
    (state: IRootState) => state.design.activeModule,
    (_state: IRootState, tabMode: "context" | "all") => tabMode,
  ],
  (all, context, module, mode) =>
    (mode === "all" ? all : context).filter(
      (tab) => module != null && ROOM_SIDEBAR_TABS_BY_MODULE[module].includes(tab)
    )
);
export const selectRoomSidebarIcons = createSelector(
  [selectVisibleRoomTabs, selectActiveSpaceHasNoWarnings],
  (iconKeys, spaceIsValid) =>
    groupValidationPanelTabs(iconKeys).map((tabKey) => {
      const base = ROOM_SIDEBAR_TAB_CONFIG[tabKey];
      if (!base) return null;

      const validation = isValidationPanelTab(tabKey);
      let icon = validation ? FaClipboardCheck : base.icon;
      let color: string | undefined;
      const tooltip = validation ? "Validering · Standardrom og artikler fra dRofus" : base.tooltip;

      // Spesialhåndtering for varslings-ikon (grønn/rød)
      if (tabKey === RoomSideBarTabs.CEILING_VALIDATIONS) {
        icon = !spaceIsValid ? InvalidIcon : base.icon;
        color = !spaceIsValid ? "red" : "#40c057";
      }

      return {
        key: tabKey,
        label: validation ? "Validering" : base.label,
        icon,
        tooltip,
        color,
      };
    })
);
